import {
  useEffect, useMemo, useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Table, TableBody, TableCell, TableHead, TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ReportFilter from './components/ReportFilter';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import ReportsMenu from './components/ReportsMenu';
import { useCatch } from '../reactHelper';
import useReportStyles from './common/useReportStyles';
import TableShimmer from '../common/components/TableShimmer';
import { formatTime } from '../common/util/formatter';
import { prefixString } from '../common/util/stringUtils';
import MapMarkers from '../map/MapMarkers';
import MapRouteCoordinates from '../map/MapRouteCoordinates';
import ReportMapPanel from './components/ReportMapPanel';
import ReportMapContent from './components/ReportMapContent';
import fetchOrThrow from '../common/util/fetchOrThrow';

const CombinedReportPage = () => {
  const { classes } = useReportStyles();
  const t = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery('(max-width:599px)');
  const isTablet = useMediaQuery('(min-width:600px) and (max-width:1023px)');
  const isTv = useMediaQuery('(min-width:1600px)');
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const devices = useSelector((state) => state.devices.items);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapExpanded, setMapExpanded] = useState(false);
  const [mapCollapseSignal, setMapCollapseSignal] = useState(0);
  const [drawerWidth, setDrawerWidth] = useState(0);
  const [mobileToolbarHeight, setMobileToolbarHeight] = useState(0);

  const parsePixelSize = (value) => {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number.parseFloat(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }

    return 0;
  };

  useEffect(() => {
    const drawerPaper = document.querySelector('.MuiDrawer-root.MuiDrawer-docked .MuiDrawer-paper');
    const appBarToolbar = document.querySelector('.MuiAppBar-root .MuiToolbar-root');

    const updateLayoutOffsets = () => {
      const measuredDrawerWidth = drawerPaper?.getBoundingClientRect().width ?? 0;
      const measuredToolbarHeight = appBarToolbar?.getBoundingClientRect().height ?? 0;
      setDrawerWidth(measuredDrawerWidth);
      setMobileToolbarHeight(measuredToolbarHeight);
    };

    updateLayoutOffsets();

    const resizeObserver = window.ResizeObserver
      ? new ResizeObserver(updateLayoutOffsets)
      : null;

    if (drawerPaper && resizeObserver) {
      resizeObserver.observe(drawerPaper);
    }

    if (appBarToolbar && resizeObserver) {
      resizeObserver.observe(appBarToolbar);
    }

    window.addEventListener('resize', updateLayoutOffsets);

    return () => {
      window.removeEventListener('resize', updateLayoutOffsets);
      resizeObserver?.disconnect();
    };
  }, []);

  const itemsCoordinates = useMemo(() => items.flatMap((item) => item.route), [items]);
  const baseExpandedInset = useMemo(() => {
    if (isPhone) {
      return 8;
    }
    if (isTablet) {
      return 12;
    }
    if (isTv) {
      return 24;
    }
    return 16;
  }, [isPhone, isTablet, isTv]);

  const expandedOffsets = useMemo(() => {
    const miniDrawerWidth = parsePixelSize(theme.spacing(8)) + 1;
    const defaultDesktopDrawerWidth = parsePixelSize(theme.dimensions.drawerWidthDesktop);
    const resolvedDesktopDrawerWidth = drawerWidth || defaultDesktopDrawerWidth || miniDrawerWidth;
    const resolvedMobileToolbarHeight = mobileToolbarHeight || parsePixelSize(theme.mixins?.toolbar?.minHeight);

    return {
      top: baseExpandedInset + (!isDesktop ? resolvedMobileToolbarHeight : 0),
      left: baseExpandedInset + (isDesktop ? resolvedDesktopDrawerWidth : 0),
      right: baseExpandedInset,
      bottom: baseExpandedInset,
    };
  }, [
    baseExpandedInset,
    drawerWidth,
    isDesktop,
    mobileToolbarHeight,
    theme,
  ]);

  const createMarkers = () => items.flatMap((item) => item.events
    .map((event) => item.positions.find((p) => event.positionId === p.id))
    .filter((position) => position != null)
    .map((position) => ({
      latitude: position.latitude,
      longitude: position.longitude,
    })));

  const onShow = useCatch(async ({ deviceIds, groupIds, from, to }) => {
    const query = new URLSearchParams({ from, to });
    deviceIds.forEach((deviceId) => query.append('deviceId', deviceId));
    groupIds.forEach((groupId) => query.append('groupId', groupId));
    setLoading(true);
    try {
      const response = await fetchOrThrow(`/api/reports/combined?${query.toString()}`);
      setItems(await response.json());
    } finally {
      setLoading(false);
    }
  });

  return (
    <PageLayout
      menu={<ReportsMenu />}
      breadcrumbs={['reportTitle', 'reportCombined']}
      onMobileDrawerToggle={(open) => {
        if (open) {
          setMapCollapseSignal((prev) => prev + 1);
        }
      }}
    >
      <div className={classes.container}>
        <ReportMapPanel
          title={t('reportCombined')}
          show={Boolean(items.length)}
          onExpandedChange={setMapExpanded}
          collapseSignal={mapCollapseSignal}
          expandedTopOffset={expandedOffsets.top}
          expandedLeftOffset={expandedOffsets.left}
          expandedRightOffset={expandedOffsets.right}
          expandedBottomOffset={expandedOffsets.bottom}
        >
          <ReportMapContent camera={Boolean(itemsCoordinates.length)} cameraProps={{ coordinates: itemsCoordinates }}>
            {items.map((item) => (
              <MapRouteCoordinates
                key={item.deviceId}
                name={devices[item.deviceId].name}
                coordinates={item.route}
                deviceId={item.deviceId}
              />
            ))}
            <MapMarkers markers={createMarkers()} />
          </ReportMapContent>
        </ReportMapPanel>
        <div
          className={classes.containerMain}
          data-has-map={Boolean(items.length)}
          data-map-expanded={mapExpanded}
        >
          <div className={classes.header}>
            <ReportFilter onShow={onShow} deviceType="multiple" loading={loading} />
          </div>
          <div className={classes.tableCard}>
            <div className={classes.tableScroller} data-has-map={Boolean(items.length)} data-map-expanded={mapExpanded}>
              <Table className={classes.table}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('sharedDevice')}</TableCell>
                    <TableCell>{t('positionFixTime')}</TableCell>
                    <TableCell>{t('sharedType')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!loading ? (items.length ? items.flatMap((item) => item.events.map((event, index) => (
                    <TableRow key={event.id}>
                      <TableCell>{index ? '' : devices[item.deviceId].name}</TableCell>
                      <TableCell>{formatTime(event.eventTime, 'seconds')}</TableCell>
                      <TableCell>{t(prefixString('event', event.type))}</TableCell>
                    </TableRow>
                  ))) : (<TableRow><TableCell colSpan={3}>{t('reportNoData')}</TableCell></TableRow>)) : (<TableShimmer columns={3} />)}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default CombinedReportPage;
